import { useGetDashboardChartsOverview } from '@forwardflow/hooks/customHooks'
import { TimePeriod } from '@forwardflow/types'
import Icon from '@forwardflow/ui/Icons'
import { Skeleton } from 'antd'
import { Link, useSearchParams } from 'react-router-dom'

const StatBar = ({
   teamPercent,
   ffPercent,
}: {
   teamPercent: number
   ffPercent: number
}) => {
   const totalBars = 55
   const teamBars = Math.round((teamPercent / 100) * totalBars)
   const ffBars = totalBars - teamBars
   const barWidth = 20
   const barHeight = 17
   const gap = 30

   return (
      <div className="flex flex-col w-full gap-2 mt-2">
         <svg
            viewBox={`0 0 ${(barWidth + gap) * totalBars} ${barHeight}`}
            width="100%"
            height={barHeight}
            className="w-full"
            preserveAspectRatio="none"
         >
            {[...Array(teamBars)].map((_, i) => (
               <rect
                  key={`team-bar-${i}`}
                  x={i * (barWidth + gap)}
                  y={0}
                  width={barWidth}
                  height={barHeight}
                  rx={2}
                  fill="#60A5FA"
               />
            ))}
            {[...Array(ffBars)].map((_, i) => (
               <rect
                  key={`ff-bar-${i}`}
                  x={(teamBars + i) * (barWidth + gap)}
                  y={0}
                  width={barWidth}
                  height={barHeight}
                  rx={2}
                  fill="#A78BFA"
               />
            ))}
         </svg>
         <div className="flex gap-4 text-xs">
            <span className="flex items-center gap-1">
               <span className="inline-block w-2 h-2 bg-blue-400 rounded-full" />
               {teamPercent}% by your team
            </span>
            <span className="flex items-center gap-1">
               <span className="inline-block w-2 h-2 bg-purple-400 rounded-full" />
               {ffPercent}% by Forward Flow
            </span>
         </div>
      </div>
   )
}

const StatRow = ({
   icon,
   value,
   change,
   label,
   responseRate,
   teamPercent,
   ffPercent,
}: {
   icon: React.ReactNode
   value: string
   change: {
      isIncrement: boolean
      percent: number
      count: number
   } | null
   label: string
   responseRate: string
   teamPercent: number
   ffPercent: number
}) => (
   <div className="flex flex-col gap-2 px-4 py-4 mb-1 rounded sm:px-10 last:border-b-0 bg-black/10">
      <div className="flex items-center gap-6">
         <div className="flex items-center gap-3 sm:min-w-[210px] border-r pr-4 md:pr-8 border-white/20">
            <span className="text-white ">{icon}</span>
            <div>
               <div className="flex items-baseline gap-2 sm:mb-1">
                  <div className="font-semibold text-white sm:text-2xl">
                     {value}
                  </div>
                  {change && (
                     <span
                        className={`${
                           change.isIncrement
                              ? 'text-[#00C2A8]'
                              : 'text-red-500'
                        } text-sm font-medium `}
                     >
                        {change.isIncrement ? '+' : '-'}
                        {change.percent}%
                     </span>
                  )}
               </div>
               <div className="text-xs text-white/70">{label}</div>
            </div>
         </div>
         <div className="flex flex-col items-start sm:min-w-[100px] md:border-r pr-4 sm:pr-8 border-white/20">
            <div className="text-lg font-semibold text-white">
               {responseRate}
            </div>
            <div className="text-xs text-white/70">Response Rate</div>
         </div>
         <div className="flex-1 hidden pl-2 md:flex">
            <StatBar teamPercent={teamPercent} ffPercent={ffPercent} />
         </div>
      </div>
   </div>
)

const StatsCard = () => {
   const [searchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'

   const {
      data: DashboardChartsOverview,
      isLoading: DashboardChartsOverviewLoading,
   } = useGetDashboardChartsOverview({
      timePeriod: timePeriod as TimePeriod,
   })

   if (DashboardChartsOverviewLoading) {
      return (
         <div className="text-white">
            <Skeleton active paragraph={{ rows: 6 }} />
         </div>
      )
   }

   const channels = DashboardChartsOverview?.stats?.channels || []

   const showInfo =
      channels.length > 0 &&
      channels.every(
         (channel) => channel.volume.contributionBreakdown.byForwardFlow < 5
      )

   const icons: Record<string, React.ReactNode> = {
      calls: (
         <Icon
            name="Call"
            width={24}
            height={24}
            className="mr-2 scale-150 md:mr-8 fill-white"
         />
      ),
      messages: <Icon name="Message" className="mr-1 md:mr-7 fill-white" />,
   }

   return (
      <div className="h-full text-white shadow-md bg-white/5 rounded-xl">
         <div className="flex items-center px-6 mb-4 font-semibold border-b-2 md:text-lg h-14 border-black/50 text-white/70">
            {DashboardChartsOverview?.stats?.displayTitle ||
               'Call & Message Stats'}
         </div>
         <div className="p-3 pt-0 md:p-5 md:pt-0 ">
            {channels.map((channel) => (
               <StatRow
                  key={channel.id}
                  icon={icons[channel.id] || null}
                  value={channel.volume.total.toString()}
                  change={channel.volume.change}
                  label={channel.text}
                  responseRate={
                     channel.volume.output
                        ? `${channel.volume.output.value}${channel.volume.output.unit}`
                        : ''
                  }
                  teamPercent={channel.volume.contributionBreakdown.byTeam}
                  ffPercent={channel.volume.contributionBreakdown.byForwardFlow}
               />
            ))}
            {showInfo && (
               <div className="flex items-center gap-2 pt-4 text-sm text-white/70">
                  <Icon name="Info" className="w-4 h-4 fill-white opacity-70" />
                  <span>
                     Forward Flow was not active for most of this period. Try
                     filtering for ‘Last Month’ to see current distribution.
                  </span>
               </div>
            )}
            {channels.length === 0 && (
               <div className="flex items-center gap-2 pt-4 text-sm text-white/70">
                  <Icon name="Info" className="w-4 h-4 fill-white opacity-70" />
                  <span>
                     No Forward Flow activity recorded for this period. Launch a{' '}
                     <Link to="/campaign" className="text-primary">
                        campaign
                     </Link>{' '}
                     to see automated performance here.
                  </span>
               </div>
            )}
         </div>
      </div>
   )
}

export default StatsCard
